// ─────────────────────────────────────────────────────────────────────────────
//  GHARPE — CONSTANTS
//  Single source of truth for colors, services, prices, and translations.
//  SAMPLE_BOOKINGS and SAMPLE_PROFESSIONALS have been removed — they were
//  hardcoded demo data that showed up alongside real Supabase data.
//  Use real data from the database in all components.
// ─────────────────────────────────────────────────────────────────────────────

// ─── UNIFIED DESIGN TOKENS ───────────────────────────────────────────────────
// One color system for the entire app.
// App.js previously used a navy/orange palette (D object) that conflicted with
// the purple palette here. Components imported from separate files should use
// this single COLORS export.
export const COLORS = {
  // Brand
  primary:      '#0F2D5C',  // navy — primary actions, headers
  primaryLight: '#1A4080',  // navy light — hover states
  primaryXL:    '#E8EEF8',  // navy tint — active tab bg

  // Accent
  accent:       '#FF5722',  // orange — CTAs, highlights
  accentLight:  '#FF7043',

  // Semantic
  success:      '#00875A',  // green — confirmations, earnings
  successLight: '#ECFDF5',
  warning:      '#F59E0B',  // amber — pending states
  warningLight: '#FEF9C3',
  danger:       '#DC2626',  // red — errors, cancellations
  dangerLight:  '#FEE2E2',

  // Neutrals
  bg:           '#F4F6FA',
  white:        '#FFFFFF',
  ink:          '#0D0D0D',
  sub:          '#545454',
  muted:        '#9A9A9A',
  border:       '#E8E8E8',

  // Legacy aliases — kept so CustomerApp/AdminApp/ProfessionalApp don't break
  // TODO: migrate those files to use the tokens above, then remove these
  primaryDark:  '#3B0764',
  accentOrange: '#F97316',
  dark:         '#1E1133',
  gray:         '#6B7280',
  grayLight:    '#E5E7EB',
  grayBg:       '#F3F4F6',
  gold:         '#F59E0B',
};

// ─── PLATFORM SPLIT ──────────────────────────────────────────────────────────
// Single place to change the revenue split — referenced by UI and translations.
export const PLATFORM_FEE_PCT  = 20; // GharPe takes 20%
export const EXPERT_EARNING_PCT = 80; // Expert receives 80%

// ─── PHONE NORMALIZER ────────────────────────────────────────────────────────
// Always strip +91 and non-digits, keep last 10 digits.
// Import this wherever phones are read from or written to the DB.
export const normalizePhone = (p = '') =>
  String(p).replace(/^\+91/, '').replace(/\D/g, '').slice(-10);

// ─── LUCKNOW AREAS ───────────────────────────────────────────────────────────
export const LUCKNOW_AREAS = [
  'Gomti Nagar','Hazratganj','Aliganj','Indira Nagar','Vikas Nagar',
  'Alambagh','Chinhat','Mahanagar','Rajajipuram','Kalyanpur',
  'Jankipuram','Faizabad Road','Kursi Road','Telibagh','Sultanpur Road',
  'Sushant Golf City','Eldeco','Vrindavan Yojana','Sarojini Nagar','Charbagh',
  'Aminabad','Chowk','Narhi','Nishatganj','Thakurganj',
  'Sitapur Road','Hardoi Road','Kanpur Road','Raibareli Road','Lucknow Cantonment',
];

// ─── SERVICES ────────────────────────────────────────────────────────────────
export const SERVICES = [
  { id: 1, name: 'Salon & Beauty', nameHi: 'सैलून & ब्यूटी', icon: '💄', color: '#FFE4E1',
    services:   ['Haircut','Facial','Waxing','Manicure','Pedicure','Makeup','Hair Color','Bleach'],
    servicesHi: ['बालों की कटाई','फेशियल','वैक्सिंग','मैनीक्योर','पेडीक्योर','मेकअप','हेयर कलर','ब्लीच'] },

  { id: 2, name: 'Cleaning', nameHi: 'सफाई', icon: '🧹', color: '#E0F2FE',
    services:   ['Home Cleaning','Sofa Cleaning','Kitchen Deep Clean','Bathroom Clean','Carpet Cleaning','Water Tank Clean'],
    servicesHi: ['घर की सफाई','सोफा सफाई','किचन क्लीन','बाथरूम क्लीन','कालीन सफाई','टंकी सफाई'] },

  { id: 3, name: 'Plumbing', nameHi: 'प्लंबिंग', icon: '🔧', color: '#FEF3C7',
    services:   ['Tap Repair','Pipe Leak Fix','Toilet Repair','Water Pump','Bathroom Fitting','Drain Cleaning'],
    servicesHi: ['नल ठीक करें','पाइप लीक','टॉयलेट रिपेयर','वाटर पंप','बाथरूम फिटिंग','ड्रेन सफाई'] },

  { id: 4, name: 'Electrician', nameHi: 'बिजली मिस्त्री', icon: '⚡', color: '#ECFDF5',
    services:   ['Fan Installation','AC Service','Wiring','Switch Board','Inverter Setup','Light Fitting'],
    servicesHi: ['पंखा लगाना','AC सर्विस','वायरिंग','स्विच बोर्ड','इन्वर्टर','लाइट फिटिंग'] },

  { id: 5, name: 'Carpenter', nameHi: 'बढ़ई', icon: '🪚', color: '#FDF4FF',
    services:   ['Furniture Repair','Door Fix','Cabinet Install','Bed Assembly','Custom Furniture','Window Repair'],
    servicesHi: ['फर्नीचर रिपेयर','दरवाजा ठीक करें','कैबिनेट','पलंग जोड़ना','कस्टम फर्नीचर','खिड़की रिपेयर'] },

  { id: 6, name: 'Pest Control', nameHi: 'कीट नियंत्रण', icon: '🐛', color: '#FFF7ED',
    services:   ['Cockroach Control','Termite Control','Rodent Control','Mosquito Control','Bed Bug Control'],
    servicesHi: ['तिलचट्टा','दीमक नियंत्रण','चूहा नियंत्रण','मच्छर नियंत्रण','खटमल नियंत्रण'] },

  { id: 7, name: 'AC Service', nameHi: 'AC सर्विस', icon: '❄️', color: '#EFF6FF',
    services:   ['AC Deep Clean','Gas Refill','AC Installation','AC Repair','AC Uninstall'],
    servicesHi: ['AC डीप क्लीन','गैस रिफिल','AC इंस्टॉलेशन','AC रिपेयर','AC हटाना'] },

  { id: 8, name: 'Painting', nameHi: 'पेंटिंग', icon: '🎨', color: '#FDF2F8',
    services:   ['Full Home Painting','Room Painting','Waterproofing','Texture Paint','Wall Putty'],
    servicesHi: ['पूरे घर की पेंटिंग','कमरे की पेंटिंग','वाटरप्रूफिंग','टेक्सचर पेंट','वॉल पुट्टी'] },
];

// ─── PRICES ──────────────────────────────────────────────────────────────────
// Keyed by English service name only.
// Hindi components must look up prices via the English name using the SERVICES
// array mapping: SERVICES[i].servicesHi[j]  →  SERVICES[i].services[j]  →  PRICES[englishName]
// This removes ~60 duplicate keys and makes price updates a one-liner.
export const PRICES = {
  // Salon & Beauty
  'Haircut': 199, 'Facial': 599, 'Waxing': 399, 'Manicure': 299,
  'Pedicure': 349, 'Makeup': 999, 'Hair Color': 799, 'Bleach': 299,

  // Cleaning
  'Home Cleaning': 799, 'Sofa Cleaning': 499, 'Kitchen Deep Clean': 899,
  'Bathroom Clean': 399, 'Carpet Cleaning': 599, 'Water Tank Clean': 699,

  // Plumbing
  'Tap Repair': 299, 'Pipe Leak Fix': 399, 'Toilet Repair': 349,
  'Water Pump': 599, 'Bathroom Fitting': 799, 'Drain Cleaning': 449,

  // Electrician
  'Fan Installation': 349, 'AC Service': 699, 'Wiring': 499,
  'Switch Board': 249, 'Inverter Setup': 449, 'Light Fitting': 199,

  // Carpenter
  'Furniture Repair': 399, 'Door Fix': 299, 'Cabinet Install': 599,
  'Bed Assembly': 499, 'Custom Furniture': 1999, 'Window Repair': 349,

  // Pest Control
  'Cockroach Control': 599, 'Termite Control': 1299, 'Rodent Control': 799,
  'Mosquito Control': 499, 'Bed Bug Control': 899,

  // AC Service
  'AC Deep Clean': 799, 'Gas Refill': 1299, 'AC Installation': 1499,
  'AC Repair': 699, 'AC Uninstall': 399,

  // Painting
  'Full Home Painting': 4999, 'Room Painting': 1999, 'Waterproofing': 2999,
  'Texture Paint': 2499, 'Wall Putty': 1499,
};

// Helper: look up price by Hindi service name
export const getPriceByHindi = (hindiName) => {
  for (const svc of SERVICES) {
    const idx = svc.servicesHi.indexOf(hindiName);
    if (idx !== -1) return PRICES[svc.services[idx]] ?? 299;
  }
  return 299; // fallback
};

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────
// yourEarnings was "25%" in the original — corrected to match the actual 80/20 split.
export const TRANSLATIONS = {
  en: {
    appName: 'GharPe', appSub: 'घर पे', tagline: 'Home Services at Your Doorstep',
    loginAs: 'LOGIN AS', customer: 'Customer', customerSub: 'Book home services',
    professional: 'Professional', professionalSub: 'View & manage jobs',
    admin: 'Admin (You)', adminSub: 'Run your business',
    search: 'Search services...', firstBooking: 'First Booking', off: '30% OFF 🎉',
    ourServices: 'Our Services', services: 'services', selectService: 'Select Service',
    continueBtn: 'Continue', whenWhere: 'When & Where?', dateLabel: 'Date',
    timeSlot: 'Time Slot', yourAddress: 'Your Address', selectArea: 'Select Area',
    addressPlaceholder: 'Enter full address...', confirmBooking: 'Confirm Booking',
    total: 'Total', payAfter: '💳 Pay after service  •  UPI / Cash accepted',
    confirmBtn: 'Confirm Booking 🎉', booked: 'Booked!',
    bookedMsg: 'Your professional will arrive at the scheduled time.',
    goHome: 'Go to Home', myBookings: 'My Bookings', home: 'Home',
    bookings: 'Bookings', profile: 'Profile', jobs: 'Jobs', earnings: 'Earnings',
    assignedJobs: 'Assigned Jobs', accept: 'Accept', navigate: 'Navigate 🗺',
    todayEarnings: "Today's Earnings", jobsToday: 'Jobs Today',
    myEarnings: 'My Earnings', totalEarnings: 'Total Earnings',
    thisWeek: 'This Week', thisMonth: 'This Month', pendingPayout: 'Pending Payout',
    dashboard: 'Dashboard', team: 'Team', manageServices: 'Manage Services',
    recentBookings: 'Recent Bookings', professionals: 'Professionals',
    totalRevenue: 'Total Revenue',
    yourEarnings: `Your Earnings (${EXPERT_EARNING_PCT}%)`,  // was wrong "25%" — now 80%
    totalBookings: 'Total Bookings', edit: 'Edit',
    today: 'Today', tomorrow: 'Tomorrow', dayAfter: 'Day After',
    rating: 'Rating', jobsDone: 'Jobs Done', area: 'Area', totalEarned: 'Total Earned',
    phone: 'Phone', email: 'Email',
  },
  hi: {
    appName: 'घरपे', appSub: 'GharPe', tagline: 'घर पर सेवाएं, आपके दरवाजे पर',
    loginAs: 'लॉगिन करें', customer: 'ग्राहक', customerSub: 'घर की सेवाएं बुक करें',
    professional: 'कर्मचारी', professionalSub: 'काम देखें और मैनेज करें',
    admin: 'एडमिन (आप)', adminSub: 'अपना बिज़नेस चलाएं',
    search: 'सेवाएं खोजें...', firstBooking: 'पहली बुकिंग', off: '30% छूट 🎉',
    ourServices: 'हमारी सेवाएं', services: 'सेवाएं', selectService: 'सेवा चुनें',
    continueBtn: 'आगे बढ़ें', whenWhere: 'कब और कहाँ?', dateLabel: 'तारीख',
    timeSlot: 'समय', yourAddress: 'आपका पता', selectArea: 'क्षेत्र चुनें',
    addressPlaceholder: 'लखनऊ में पूरा पता दर्ज करें...', confirmBooking: 'बुकिंग कन्फर्म करें',
    total: 'कुल', payAfter: '💳 सेवा के बाद भुगतान  •  UPI / कैश स्वीकार',
    confirmBtn: 'बुकिंग कन्फर्म करें 🎉', booked: 'बुक हो गया!',
    bookedMsg: 'आपका कर्मचारी तय समय पर आएगा।',
    goHome: 'होम पर जाएं', myBookings: 'मेरी बुकिंग', home: 'होम',
    bookings: 'बुकिंग', profile: 'प्रोफाइल', jobs: 'काम', earnings: 'कमाई',
    assignedJobs: 'मिले हुए काम', accept: 'स्वीकार करें', navigate: 'नेविगेट 🗺',
    todayEarnings: 'आज की कमाई', jobsToday: 'आज के काम',
    myEarnings: 'मेरी कमाई', totalEarnings: 'कुल कमाई',
    thisWeek: 'इस हफ्ते', thisMonth: 'इस महीने', pendingPayout: 'बकाया भुगतान',
    dashboard: 'डैशबोर्ड', team: 'टीम', manageServices: 'सेवाएं मैनेज करें',
    recentBookings: 'हाल की बुकिंग', professionals: 'कर्मचारी',
    totalRevenue: 'कुल आय',
    yourEarnings: `आपकी कमाई (${EXPERT_EARNING_PCT}%)`,  // was wrong "25%" — now 80%
    totalBookings: 'कुल बुकिंग', edit: 'संपादित करें',
    today: 'आज', tomorrow: 'कल', dayAfter: 'परसों',
    rating: 'रेटिंग', jobsDone: 'काम पूरे', area: 'क्षेत्र', totalEarned: 'कुल कमाई',
    phone: 'फोन', email: 'ईमेल',
  },
};
